export type ZoneMatchLabel =
  | 'Perfect Match'
  | 'Slightly Undersized'
  | 'Slightly Oversized'
  | 'Undersized'
  | 'Oversized'
  | '—';

export type ZoneMatchVariant =
  | 'perfect'
  | 'over'
  | 'oversized'
  | 'slightlyUnder'
  | 'under'
  | 'unknown';

export type ZoneMatchResult = {
  label: ZoneMatchLabel;
  variant: ZoneMatchVariant;
};

export type MatchThresholds = {
  lowerPercent: number;
  upperPercent: number;
};

export type ZoneSizingThresholds = {
  cooling: MatchThresholds;
  heating: MatchThresholds;
};

export type MatchSideRule = 'default' | 'excluded' | 'notUndersized';

export type ZoneMatchSideRules = {
  heating: MatchSideRule;
  cooling: MatchSideRule;
};

// Worst first: a unit that cannot carry the load, then one so large it will
// short-cycle and (for cooling) leave the air humid, then the two "slightly"
// tiers just outside the band, then a match inside it.
export const MATCH_VARIANT_SEVERITY: Record<
  Exclude<ZoneMatchVariant, 'unknown'>,
  number
> = {
  under: 0,
  oversized: 1,
  slightlyUnder: 2,
  over: 3,
  perfect: 4
};

// Below this the unit is undersized outright, whatever the band's lower
// bound; above the band's upper bound by this many points it is oversized
// outright — the same distance past the band as 70 sits under the default
// lower bound of 90.
const UNDERSIZED_BELOW_PERCENT = 70;
export const OVERSIZED_MARGIN_POINTS = 25;

const PERFECT_MATCH: ZoneMatchResult = {
  label: 'Perfect Match',
  variant: 'perfect'
};

const UNDERSIZED_VARIANTS: ReadonlySet<ZoneMatchVariant> = new Set([
  'under',
  'slightlyUnder'
]);

export function classifyMatch(
  percent: number,
  thresholds?: MatchThresholds
): ZoneMatchResult {
  if (percent < UNDERSIZED_BELOW_PERCENT) {
    return { label: 'Undersized', variant: 'under' };
  }
  const lower = thresholds?.lowerPercent ?? 90;
  const upper = thresholds?.upperPercent ?? 120;
  if (percent < lower) {
    return { label: 'Slightly Undersized', variant: 'slightlyUnder' };
  }
  if (percent > upper + OVERSIZED_MARGIN_POINTS) {
    return { label: 'Oversized', variant: 'oversized' };
  }
  if (percent > upper) {
    return { label: 'Slightly Oversized', variant: 'over' };
  }
  return PERFECT_MATCH;
}

function applySideRule(
  result: ZoneMatchResult,
  rule: MatchSideRule
): ZoneMatchResult | null {
  if (rule === 'excluded') {
    return null;
  }
  if (rule === 'notUndersized' && UNDERSIZED_VARIANTS.has(result.variant)) {
    return PERFECT_MATCH;
  }
  return result;
}

export function classifyZoneMatch(
  heatingMatchPercent: number,
  coolingMatchPercent: number,
  sizingThresholds?: ZoneSizingThresholds,
  sideRules?: ZoneMatchSideRules
): ZoneMatchResult | null {
  const heating = applySideRule(
    classifyMatch(heatingMatchPercent, sizingThresholds?.heating),
    sideRules?.heating ?? 'default'
  );
  const cooling = applySideRule(
    classifyMatch(coolingMatchPercent, sizingThresholds?.cooling),
    sideRules?.cooling ?? 'default'
  );
  if (heating === null) {
    return cooling;
  }
  if (cooling === null) {
    return heating;
  }
  return MATCH_VARIANT_SEVERITY[heating.variant] <=
    MATCH_VARIANT_SEVERITY[cooling.variant]
    ? heating
    : cooling;
}
